import { useFirestoreOrderBy } from "../../firebase/useFirestore";
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { db, timestamp } from "../../firebase/config.js"
import uuid from "react-uuid";
import { client } from "../../hooks/Client"
import { useState, useEffect, useContext } from "react";
import { Auth } from "../../StateManagment/Auth";
import DeleteModal from "../common/DeleteModal";
import softDeleteDoc from "../../helpers/softDeleteDoc";
import Tooltip from "../../components/common/Tooltip";
import Table from "../../components/common/table/Table.jsx";
import TableCell from "../../components/common/table/TableCell.jsx";
import TableDragHandleCell from "../../components/common/table/TableDragHandleCell.jsx";
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined';
import StatusConfigTable from './StatusConfigTable';
import SettingsGroup from './SettingsGroup';
import MeasurementMomentMailSetting from './MeasurementMomentMailSetting';
import { PERSONA_STATUS_LABELS } from '../../helpers/personas/personaStatusLabels';
import useAutosaveField from '../../hooks/useAutosaveField';
import AutosaveStatus from '../common/AutosaveStatus';
import parseTariff from '../../helpers/personas/parseTariff';

/**
 * Instellingen → tabblad 'Ondersteuning' → groep 'Deelnemers': de mailschakelaar
 * voor meetmomenten, de statussen van deelnemers en de tabel met trajecten.
 *
 * Tot 2026-10-08 stonden hier ook listeners en handlers voor `PersonaMethodes`
 * en `PersonaReportStandard` zonder enige weergave: twee Firestore-listeners
 * per bezoek aan dit tabblad die niets voedden (inbox #304).
 *
 * @param {object} props
 * @param {string} props.selectedOption - Het gekozen tabblad van de instellingen; dit onderdeel rendert alleen bij '6'
 * @returns {JSX.Element}
 */
const Personas = ({selectedOption}) => {
    // State
    const [auth] = useContext(Auth)
    const [dragArrayRoutes, setDragArrayRoutes] = useState([]);
    const [deleteModal, setDeleteModal] = useState(false)
    const [deleteDocid, setDeleteDocid] = useState('')
    const [deleteName, setDeleteName] = useState('')

    // Titel, code en tarief niet per toetsaanslag schrijven: Firestore zet
    // schrijfacties naar hetzelfde document achter elkaar in de rij, en wie
    // wegnavigeert verliest wat er nog in staat. useAutosaveField wacht tot de
    // gebruiker even stopt én schrijft alsnog weg bij het verlaten van de
    // pagina. docs/inbox.md #293.
    // AI-CONTEXT: See docs/pitfalls.md#state-per-keystroke-write-loses-the-tail
    const { save, status } = useAutosaveField('PersonaRoutes')

    // Firestore
    const routes = useFirestoreOrderBy('PersonaRoutes', 'Position', 'asc')

    useEffect(() => {
        setDragArrayRoutes(routes || []);
    }, [routes]);

    const addRoute = () => {

        db.collection('PersonaRoutes')
        .add({
            ID: uuid(),
            Compagny: client,
            CompagnyID: client,
            Timestamp: timestamp,
            Title: '',
            Position: routes.length + 1,
        })

    }

    const changeRouteTitle = (e) => {
        save(e.target.dataset.docid, { Title: e.target.value })
    }

    const codeHandler = (e) => {
        save(e.target.dataset.docid, { Code: e.target.value })
    }

    // Een leeggemaakt veld blijft leeg (''), nooit NaN: `parseInt('')` schreef
    // NaN weg bij 13 trajecten platformbreed. docs/inbox.md #292.
    const tariffHandler = (e) => {
        save(e.target.dataset.docid, { Tariff: parseTariff(e.target.value) })
    }

    const unitHandler = (e) => {

        const docid = e.target.dataset.docid
        const value = e.target.options[e.target.selectedIndex].value

        db.collection('PersonaRoutes')
        .doc(docid)
        .update({
            Unit: value
        })
    }

    // Eerst vragen, dan zacht verwijderen (ADR-008): het was één klik met een
    // harde delete, en de Prullenbak beloofde iets terug te zetten wat er
    // niet in stond (testronde 2026-10-08, B1; inbox #304).
    // AI-CONTEXT: See docs/pitfalls.md#ui-soft-delete-promises-a-trash-it-is-not-in
    const deleteModalHandler = (e) => {
        setDeleteDocid(e.currentTarget.dataset.docid)
        setDeleteName(e.currentTarget.dataset.deletename || 'dit traject')
        setDeleteModal(true)
    }

    const deleteRoute = () => {
        softDeleteDoc({ collection: 'PersonaRoutes', docid: deleteDocid, authId: auth?.ID, title: deleteName })
    }

    const handleDragEndRoutes = (result) => {
        if (!result.destination) return;

        const items = Array.from(dragArrayRoutes);
        const [movedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, movedItem);

        setDragArrayRoutes(items);

        items.forEach((route, idx) => {
            db.collection('PersonaRoutes')
            .doc(route.docid)
            .update({ Position: idx + 1 })
        })
    }

  return (
    <>
        {selectedOption === '6' &&
            <div className="settings-container">
                <SettingsGroup id="settings-deelnemers" title="Deelnemers">
                {/* Bovenaan: onder de statustabel viel hij buiten beeld voor wie
                    niet scrolt (testronde 2026-10-01, B3). */}
                <MeasurementMomentMailSetting />
                <StatusConfigTable
                    title="Statussen van deelnemers"
                    description="Geef een status je eigen naam, of haal een status die je niet gebruikt uit beeld."
                    canonicalLabels={PERSONA_STATUS_LABELS}
                    field="PersonaStatusConfig"
                />
                {/* `dashboard-container` en niet `table-container`: dat laatste
                    was tot 2026-10-14 een scrollvak van 520px; met een kop, uitleg en
                    statusregel erbij viel de rest buiten beeld.
                    AI-CONTEXT: See docs/pitfalls.md#ui-container-class-is-a-scroll-box */}
                {/* id: doel van de link 'Eenheid van het traject aanpassen' op de pagina Facturatie (skipFix). */}
                <div className="dashboard-container" id="settings-trajecten">
                <h2>Trajecten</h2>
                {/* Eén zin onder de kop, zoals de buurblokken: een nieuwe beheerder
                    herkende het blok alleen aan de bestaande rij 'WMO'
                    (testronde 2026-10-08, B2). */}
                <p>De regeling of financiering waaronder een deelnemer meedoet, zoals WMO of PGB, met het tarief dat ervoor geldt.</p>
                {/* Geen opslaanknop op dit scherm, dus dit is het enige signaal
                    dat het bewaard is. `reserve`: in rust leeg maar wél aanwezig,
                    anders zakt de tabel een regel bij de eerste opslag. */}
                <AutosaveStatus status={status} reserve />
                <Table
                    tableClassName="persona-routes-table"
                    columns={[
                        { key: 'drag', label: '', searchable: false },
                        { key: 'title', label: 'TITEL' },
                        { key: 'code', label: 'CODE', searchable: false },
                        { key: 'tariff', label: 'TARIEF', searchable: false },
                        { key: 'unit', label: 'EENHEID', searchable: false },
                        { key: 'delete', label: 'VERWIJDEREN', searchable: false },
                    ]}
                    items={dragArrayRoutes || []}
                    getItemKey={(route) => route.ID}
                    renderRow={(route) => (
                        <>
                            <TableDragHandleCell>
                                <DragIndicatorOutlinedIcon />
                            </TableDragHandleCell>
                            <TableCell>
                                {/* Volle breedte: op 80% toonde het veld 'TEST-2026-10-08 Regeli'
                                    terwijl de kolom ruimte genoeg had (testronde 2026-10-08, B4). */}
                                <Tooltip content={route.Title} width='100%' top='35px'>
                                    <input type="text" data-docid={route.docid} defaultValue={route.Title} onChange={changeRouteTitle} />
                                </Tooltip>
                            </TableCell>
                            <TableCell>
                                <input type="text" data-docid={route.docid} defaultValue={route.Code} onChange={codeHandler} />
                            </TableCell>
                            <TableCell>
                                <input type="number" data-docid={route.docid} defaultValue={route.Tariff} onChange={tariffHandler} />
                            </TableCell>
                            <TableCell>
                                <select value={route.Unit} data-docid={route.docid} onChange={unitHandler}>
                                    <option value=""> -- Selecteer een eenheid --</option>
                                    <option value="hour">Uur</option>
                                    <option value="halfday">Dagdeel</option>
                                    <option value="day">Dag</option>
                                    <option value="week">Week</option>
                                    <option value="month">Maand</option>
                                    <option value="year">Jaar</option>
                                </select>
                            </TableCell>
                            <TableCell>
                                <Tooltip content={'Verwijder traject'} width='80%' top='-60px'>
                                    <DeleteOutlinedIcon className='table-delete-icon' data-testid={`verwijder-${route.ID}`} data-id={route.ID} data-docid={route.docid} data-deletename={route.Title} onClick={deleteModalHandler} />
                                </Tooltip>
                            </TableCell>
                        </>
                    )}
                    searchAccessor={(route) => `${route?.Title || ''} ${route?.Code || ''}`}
                    searchPlaceholder="Zoeken op traject"
                    emptyMessage="Nog geen trajecten toegevoegd."
                    draggable
                    droppableId="personaRoutes"
                    onDragEnd={handleDragEndRoutes}
                    addRow={{
                        toolTipContent: 'Traject toevoegen',
                        onClick: addRoute,
                    }}
                />
                <DeleteModal
                    deleteModal={deleteModal}
                    setDeleteModal={setDeleteModal}
                    deleteItem={deleteRoute}
                    deleteName={deleteName}
                    softDelete
                />
                </div>
                </SettingsGroup>
            </div>
        }
    </>
  )
}

export default Personas
